/**
 * Reset Password Page
 *
 * Shown after clicking the password reset link from email.
 * Allows user to set a new password using Supabase's updateUser API.
 */

import { useNavigate } from '@solidjs/router';
import { type Component, createSignal, onMount, Show } from 'solid-js';
import { supabase, updatePassword } from '../lib/supabase';

const ResetPassword: Component = () => {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = createSignal('');
  const [confirmPassword, setConfirmPassword] = createSignal('');
  const [error, setError] = createSignal<string | null>(null);
  const [loading, setLoading] = createSignal(false);
  const [success, setSuccess] = createSignal(false);
  const [_sessionReady, setSessionReady] = createSignal(false);

  // Ensure we have a valid session before allowing password reset
  onMount(async () => {
    if (!supabase) {
      setError('Supabase not configured');
      return;
    }

    // Check for session
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (session) {
      setSessionReady(true);
    } else {
      // No session - the recovery link may have expired or wasn't processed
      setError('Your password reset link has expired. Please request a new one.');
    }
  });

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    setError(null);

    if (newPassword() !== confirmPassword()) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword().length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await updatePassword(newPassword());

      if (updateError) {
        // Provide more helpful error messages
        if (updateError.message.includes('same as')) {
          setError('New password must be different from your current password');
        } else if (updateError.message.includes('422')) {
          setError(
            'Password update failed. Your reset link may have expired. Please request a new one.',
          );
        } else {
          setError(updateError.message);
        }
        setLoading(false);
        return;
      }

      setSuccess(true);
      setLoading(false);
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update password');
      setLoading(false);
    }
  };

  return (
    <div class="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-950 px-4">
      <div class="w-full max-w-md">
        <div class="bg-gray-800/50 border border-gray-700 rounded-2xl p-8">
          <div class="text-center mb-6">
            <h1 class="text-2xl font-bold text-white">Set New Password</h1>
            <p class="text-gray-400 text-sm mt-2">Enter your new password below</p>
          </div>

          <Show when={success()}>
            <div class="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-xl">
              <p class="text-green-400 text-center">
                Password updated successfully! Redirecting...
              </p>
            </div>
          </Show>

          <Show when={error()}>
            <div class="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-xl">
              <p class="text-red-400 text-center text-sm">{error()}</p>
            </div>
          </Show>

          <Show when={!success()}>
            <form onSubmit={handleSubmit} class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1.5">New Password</label>
                <input
                  type="password"
                  value={newPassword()}
                  onInput={(e) => setNewPassword(e.currentTarget.value)}
                  placeholder="Enter new password"
                  minLength={8}
                  required
                  class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1.5">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword()}
                  onInput={(e) => setConfirmPassword(e.currentTarget.value)}
                  placeholder="Confirm new password"
                  minLength={8}
                  required
                  class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <button
                type="submit"
                disabled={loading() || !newPassword() || !confirmPassword()}
                class="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-purple-500/20"
              >
                {loading() ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </Show>

          <div class="mt-6 text-center">
            <button
              onClick={() => navigate('/login', { replace: true })}
              class="text-purple-400 hover:text-purple-300 text-sm"
            >
              Back to login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
